import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import {
  analyzeProfileCompleteness,
  ProfileCompletenessScore,
} from "@/services/profileCompletenessService";

interface ProfileCompletenessContextType {
  score: ProfileCompletenessScore | null;
  isLoading: boolean;
  refreshScore: () => Promise<void>;
  shouldShowNudge: boolean;
  dismissNudge: () => void;
  isDismissed: boolean;
}

const ProfileCompletenessContext = createContext<
  ProfileCompletenessContextType | undefined
>(undefined);

export function ProfileCompletenessProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user, userRole } = useAuth();
  const [score, setScore] = useState<ProfileCompletenessScore | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const refreshScore = async () => {
    if (!user || !userRole) return;

    setIsLoading(true);
    try {
      const newScore = await analyzeProfileCompleteness(user.id, userRole);
      setScore(newScore);
    } catch (error) {
      console.error("Failed to refresh profile completeness score:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const dismissNudge = () => {
    setIsDismissed(true);
    // Store dismissal in localStorage with timestamp
    localStorage.setItem(
      `profile_nudge_dismissed_${user?.id}`,
      Date.now().toString(),
    );
  };

  const shouldShowNudge = React.useMemo(() => {
    if (!score || isDismissed) return false;

    // Don't show if profile is already complete
    if (score.overall >= 90) return false;

    // Check if recently dismissed (within 24 hours)
    const dismissedTime = localStorage.getItem(
      `profile_nudge_dismissed_${user?.id}`,
    );
    if (dismissedTime) {
      const hoursAgo =
        (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60);
      if (hoursAgo < 24) return false;
    }

    return true;
  }, [score, isDismissed, user?.id]);

  useEffect(() => {
    if (user && userRole) {
      refreshScore();
    }
  }, [user, userRole]);

  // Check for previous dismissal on mount
  useEffect(() => {
    if (user) {
      const dismissedTime = localStorage.getItem(
        `profile_nudge_dismissed_${user.id}`,
      );
      if (dismissedTime) {
        const hoursAgo =
          (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60);
        setIsDismissed(hoursAgo < 24);
      }
    }
  }, [user]);

  const value = {
    score,
    isLoading,
    refreshScore,
    shouldShowNudge,
    dismissNudge,
    isDismissed,
  };

  return (
    <ProfileCompletenessContext.Provider value={value}>
      {children}
    </ProfileCompletenessContext.Provider>
  );
}

export function useProfileCompleteness() {
  const context = useContext(ProfileCompletenessContext);
  if (context === undefined) {
    throw new Error(
      "useProfileCompleteness must be used within a ProfileCompletenessProvider",
    );
  }
  return context;
}
